import { useCallback, useMemo } from 'react';
import { Locale, useGetProdBeDomain, useLocale } from '@ae/shared';
import {
  AEXCategory,
  AEXSection,
  LinkGetter,
  useBuildAEXUrl,
  useLink,
} from '../hooks';

export enum AEXLink {
  Activities,
  Hikes,
  ExperiencesNearRental,
  Search,
  WalksNearRental,
  SitytrailDetails,
}

export const useGetAEXLink = () => {
  const locale = useLocale();
  const getProdBeDomain = useGetProdBeDomain();
  const { buildUrl: buildAEXUrl } = useBuildAEXUrl();

  const aexDomain = useMemo(() => {
    switch (locale) {
      case Locale.En:
      case Locale.De:
        return `${getProdBeDomain(Locale.En)}`;
      case Locale.Fr:
        return `${getProdBeDomain(Locale.Fr)}`;
      case Locale.Nl:
      default:
        return `${getProdBeDomain(Locale.Nl)}`;
    }
  }, [getProdBeDomain, locale]);

  const getLink = useCallback(
    (link: AEXLink): LinkGetter => {
      switch (link) {
        case AEXLink.Activities:
          return { href: `${getProdBeDomain(locale)}/experience` };
        case AEXLink.Hikes:
          return {
            href: () => {
              switch (locale) {
                case Locale.En:
                case Locale.De:
                  return `${getProdBeDomain(Locale.En)}/experience/hike`;
                case Locale.Fr:
                  return `${getProdBeDomain(Locale.Fr)}/experience/balade`;
                case Locale.Nl:
                default:
                  return `${getProdBeDomain(Locale.Nl)}/experience/wandeling`;
              }
            },
          };
        case AEXLink.ExperiencesNearRental:
          return {
            href: (lat: string, long: string, category: AEXCategory) =>
              buildAEXUrl({
                domain: aexDomain,
                section: AEXSection.Experience,
                locale,
                lat,
                long,
                category,
                rate: 60,
              }),
          };
        case AEXLink.Search:
          return {
            href: (
              lat: string,
              long: string,
              rate: number,
              category: AEXCategory
            ) =>
              buildAEXUrl({
                domain: aexDomain,
                section: AEXSection.Experience,
                locale,
                lat,
                long,
                rate,
                category,
              }),
          };
        case AEXLink.WalksNearRental:
          return {
            href: (lat: string, long: string) =>
              buildAEXUrl({
                domain: aexDomain,
                section: AEXSection.Walk,
                locale,
                lat,
                long,
                rate: 60,
              }),
          };
        case AEXLink.SitytrailDetails:
          return {
            href: () => {
              switch (locale) {
                case Locale.En:
                case Locale.De:
                  return `${getProdBeDomain(Locale.En)}/experience/what-is-the-sitytrail-advantage-at-ardennes-etape`;
                case Locale.Fr:
                  return `${getProdBeDomain(Locale.Fr)}/experience/news/quest-ce-que-lavantage-sitytrail`;
                case Locale.Nl:
                default:
                  return `${getProdBeDomain(Locale.Nl)}/experience/news/wat-het-sitytrail-voordeel`;
              }
            },
          };
        default:
          throw Error('link not found');
      }
    },
    [buildAEXUrl, getProdBeDomain, locale]
  );

  return useLink<AEXLink>(getLink);
};
